import { onCLS, onFCP, onINP, onLCP, onTTFB, Metric } from 'web-vitals';
import { logger } from './logger';

interface PerformanceMemory {
  usedJSHeapSize: number;
  totalJSHeapSize: number;
  jsHeapSizeLimit: number;
}

declare global {
  interface Performance {
    memory?: PerformanceMemory;
  }
}

// Web Vitalsのしきい値（良好・改善が必要・不良）
const THRESHOLDS = {
  CLS: { good: 0.1, needsImprovement: 0.25 }, // Cumulative Layout Shift
  FCP: { good: 1800, needsImprovement: 3000 }, // First Contentful Paint (ms)
  INP: { good: 200, needsImprovement: 500 }, // Interaction to Next Paint (ms)
  LCP: { good: 2500, needsImprovement: 4000 }, // Largest Contentful Paint (ms)
  TTFB: { good: 800, needsImprovement: 1800 } // Time to First Byte (ms)
} as const;

type MetricName = keyof typeof THRESHOLDS;
type Rating = 'good' | 'needs-improvement' | 'poor' | 'unknown';

/**
 * メトリクスの評価を行う
 * @param name - メトリクス名
 * @param value - 測定値
 * @returns - 'good' | 'needs-improvement' | 'poor'
 */
const rateMetric = (name: string, value: number): Rating => {
  const threshold = THRESHOLDS[name as MetricName];
  if (!threshold) return 'unknown';

  if (value <= threshold.good) return 'good';
  if (value <= threshold.needsImprovement) return 'needs-improvement';
  return 'poor';
};

/**
 * Web Vitalsメトリクスをログに記録
 * @param metric - Web Vitalsメトリクスオブジェクト
 */
const logWebVital = (metric: Metric): void => {
  const { name, value, delta, entries } = metric;
  const rating = rateMetric(name, value);

  // パフォーマンスログに記録
  logger.performance(`Web Vitals: ${name}`, value, {
    metric: name,
    value: Math.round(value),
    delta: Math.round(delta),
    rating,
    unit: name === 'CLS' ? '' : 'ms',
    entries: entries?.length || 0
  });

  // 評価が悪い場合は警告
  if (rating === 'poor') {
    logger.warn(`パフォーマンス警告: ${name}が基準値を超えています`, {
      metric: name,
      value: Math.round(value),
      threshold: THRESHOLDS[name as MetricName]?.needsImprovement,
      rating
    });
  }

  // 開発環境では詳細をコンソールに出力
  if (process.env.NODE_ENV === 'development') {
    console.log(`[Web Vitals] ${name}:`, {
      value: `${Math.round(value)}${name === 'CLS' ? '' : 'ms'}`,
      rating,
      delta: Math.round(delta),
      entries: entries
    });
  }
};

/**
 * カスタムメトリクスの記録
 * @param name - メトリクス名
 * @param value - 測定値
 * @param metadata - 追加のメタデータ
 */
export const recordCustomMetric = (
  name: string,
  value: number,
  metadata: Record<string, any> = {}
): void => {
  logger.performance(`Custom Metric: ${name}`, value, {
    metric: name,
    value: Math.round(value),
    custom: true,
    ...metadata
  });
};

/**
 * コンポーネントのレンダリング時間を測定
 * @param componentName - コンポーネント名
 * @returns - 測定終了関数
 */
export const measureComponentRender = (componentName: string): (() => void) => {
  const startTime = performance.now();

  return () => {
    const duration = performance.now() - startTime;
    recordCustomMetric('component-render', duration, {
      component: componentName,
      unit: 'ms'
    });
  };
};

/**
 * API呼び出しのパフォーマンスを測定
 * @param endpoint - APIエンドポイント
 * @returns - 測定終了関数
 */
export const measureApiCall = (endpoint: string): ((success?: boolean) => void) => {
  const startTime = performance.now();

  return (success: boolean = true) => {
    const duration = performance.now() - startTime;
    recordCustomMetric('api-call', duration, {
      endpoint,
      success,
      unit: 'ms'
    });
  };
};

/**
 * メモリ使用量の記録
 */
export const recordMemoryUsage = (): void => {
  if (performance.memory) {
    const { usedJSHeapSize, totalJSHeapSize, jsHeapSizeLimit } = performance.memory;
    const usagePercent = (usedJSHeapSize / jsHeapSizeLimit) * 100;

    recordCustomMetric('memory-usage', usagePercent, {
      used: Math.round(usedJSHeapSize / 1024 / 1024),
      total: Math.round(totalJSHeapSize / 1024 / 1024),
      limit: Math.round(jsHeapSizeLimit / 1024 / 1024),
      unit: 'MB'
    });
  }
};

interface VitalsCollection {
  [key: string]: number;
}

/**
 * ページのパフォーマンススコアを計算
 * @param vitals - 収集したWeb Vitals
 * @returns - スコア（0-100）
 */
export const calculatePerformanceScore = (vitals: VitalsCollection): number => {
  const weights: Record<string, number> = {
    LCP: 0.25,
    FCP: 0.15,
    CLS: 0.25,
    INP: 0.25,
    TTFB: 0.1
  };

  let totalScore = 0;
  let totalWeight = 0;

  Object.entries(vitals).forEach(([metric, value]) => {
    const weight = weights[metric] || 0;
    if (weight > 0) {
      const rating = rateMetric(metric, value);
      const score = rating === 'good' ? 100 : rating === 'needs-improvement' ? 50 : 0;
      totalScore += score * weight;
      totalWeight += weight;
    }
  });

  return totalWeight > 0 ? Math.round(totalScore / totalWeight) : 0;
};

/**
 * Web Vitalsの測定を開始
 */
export const initWebVitals = (): void => {
  // Core Web Vitalsの測定
  onCLS(logWebVital);
  onINP(logWebVital);
  onLCP(logWebVital);

  // 追加のメトリクス
  onFCP(logWebVital);
  onTTFB(logWebVital);

  // 定期的なメモリ使用量の記録（開発環境のみ）
  if (process.env.NODE_ENV === 'development') {
    setInterval(recordMemoryUsage, 30000); // 30秒ごと
  }

  // ページ離脱時にサマリーを送信
  window.addEventListener('beforeunload', () => {
    logger.info('パフォーマンスサマリー', {
      sessionDuration: Math.round(performance.now()),
      navigationCount: performance.getEntriesByType('navigation').length
    });
  });
};

// パフォーマンス向上のためのユーティリティ関数
export const debounce = <T extends (...args: any[]) => any>(
  func: T,
  wait: number
): ((...args: Parameters<T>) => void) => {
  let timeout: ReturnType<typeof setTimeout> | null = null;

  return function executedFunction(this: any, ...args: Parameters<T>) {
    const context = this;
    const later = () => {
      timeout = null;
      func.apply(context, args);
    };

    if (timeout !== null) {
      clearTimeout(timeout);
    }
    timeout = setTimeout(later, wait);
  };
};

export const throttle = <T extends (...args: any[]) => any>(
  func: T,
  limit: number
): ((...args: Parameters<T>) => void) => {
  let inThrottle = false;

  return function (this: any, ...args: Parameters<T>) {
    if (!inThrottle) {
      func.apply(this, args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
};
